import { Box, Flex, Text } from '@chakra-ui/react';
import type React from 'react';
import { useCallback, useState } from 'react';
import { MdError } from 'react-icons/md';
import { DownloadIcon } from '@/assets/icons';
import { colors } from '@/components/ui/theme';
import { Tooltip } from '@/components/ui/tooltip';

// Comprehensive list of accepted file types
export const ACCEPTED_FILE_TYPES = {
	// PDFs
	pdf: '.pdf',
	// Word Documents
	doc: '.doc',
	docx: '.docx',
	// Images
	jpg: '.jpg',
	jpeg: '.jpeg',
	png: '.png',
	gif: '.gif',
	bmp: '.bmp',
	tiff: '.tiff',
	webp: '.webp',
	// Excel/Spreadsheets
	xls: '.xls',
	xlsx: '.xlsx',
	xlsm: '.xlsm',
	csv: '.csv',
	// Apple iWork
	pages: '.pages',
	numbers: '.numbers',
	// Text files
	txt: '.txt',
	rtf: '.rtf',
	// RFX formats (RFP, RFQ, RFI)
	rfp: '.rfp',
	rfq: '.rfq',
	rfi: '.rfi',
	// Compressed files
	zip: '.zip',
} as const;

export const DEFAULT_ACCEPTED_EXTENSIONS = Object.values(ACCEPTED_FILE_TYPES).join(',');

export interface FaceliftDragDropProps {
	onFilesUpload: (files: FileList) => void;
	accept?: string;
	multiple?: boolean;
	disabled?: boolean;
	maxSize?: number; // in bytes
	error?: string;
	disabledMessage?: string; // Custom message to show when disabled
}

const FaceliftDragDrop: React.FC<FaceliftDragDropProps> = ({
	onFilesUpload,
	accept = DEFAULT_ACCEPTED_EXTENSIONS,
	multiple = true,
	disabled = false,
	maxSize,
	error,
	disabledMessage = 'Please fill in all required fields before uploading files',
}) => {
	const [isDragOver, setIsDragOver] = useState(false);
	const [validationError, setValidationError] = useState<string>('');

	const validateFiles = useCallback(
		(files: FileList): boolean => {
			const acceptedExtensions = accept?.toLowerCase()?.split(',').map(ext => ext.trim());
			
			// Validate file types
			for (const file of Array.from(files)) {
				const fileName = file.name.toLowerCase();
				const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
				
				if (!acceptedExtensions.includes(fileExtension)) {
					setValidationError(
						`This file type is not supported. Please upload a valid file format (${file.name}).`
					);
					return false;
				}
			}

			// Validate file sizes
			if (maxSize) {
				for (const file of Array.from(files)) {
					if (file.size > maxSize) {
						const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
						setValidationError(`File size must not exceed ${maxSizeMB} MB`);
						return false;
					}
				}
			}
			
			setValidationError('');
			return true;
		},
		[maxSize, accept],
	);

	const handleFileUpload = useCallback(
		(files: FileList | null) => {
			if (!files || disabled) return;

			if (validateFiles(files)) {
				setValidationError(''); // Clear error on successful upload
				onFilesUpload(files);
			}
		},
		[onFilesUpload, disabled, validateFiles],
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			if (!disabled) {
				setIsDragOver(true);
			}
		},
		[disabled],
	);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragOver(false);
			if (!disabled) {
				handleFileUpload(e.dataTransfer.files);
			}
		},
		[handleFileUpload, disabled],
	);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleFileUpload(e.target.files);
			// Reset input value to allow re-uploading same file
			e.target.value = '';
		},
		[handleFileUpload],
	);

	const displayError = validationError || error;
	const tooltipContent = disabled ? disabledMessage : displayError;

	return (
		<Box width="full" position="relative" zIndex={displayError ? 1000 : 1}>
			<Tooltip
				showArrow
				portalled={false}
				contentProps={{ css: { '--tooltip-bg': disabled ? 'colors.brand.solid' : 'colors.brand.errors', 'z-index': '1001', display: tooltipContent ? 'block' : 'none' } }}
				color="white"
				open={displayError ? true : disabled ? undefined : false} // Force open for errors, hover for disabled, hidden otherwise
				content={tooltipContent}
				positioning={{ placement: 'top-center' }}
			>
				<Box
					border="2px dashed"
					borderColor={
						displayError
							? 'brand.errors'
							: isDragOver && !disabled
								? 'brand.solid'
								: 'brand.input.placeholder'
					}
					borderRadius="md"
					p={{ base: 2, sm: 2, md: 3 }}
					textAlign="center"
					bg={isDragOver && !disabled ? 'brand.subtle' : 'brand.input.bg'}
					transition="all"
					cursor={disabled ? 'not-allowed' : 'pointer'}
					position="relative"
					opacity={disabled ? 0.5 : 1}
					minHeight={{ base: '10', sm: '9', md: '8' }}
					width="full"
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					<input
						type="file"
						multiple={multiple}
						onChange={handleFileInputChange}
						onClick={() => setValidationError('')}
						disabled={disabled}
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							opacity: 0,
							cursor: disabled ? 'not-allowed' : 'pointer',
						}}
						accept={accept}
					/>

					<Flex
						direction="row"
						align="center"
						justify="start"
						gap={{ base: 2, md: 3 }}
						width="full"
						height="full"
						py={{ base: 0.5, sm: 1, md: 1.5 }}
					>
						<Box color="brand.input.text" flexShrink={0}>
							<DownloadIcon width={18} height={18} />
						</Box>
						<Flex flex={1} align="center" gap={2}>
							<Text
								fontSize={{ base: 'sm', md: 'sm' }}
								fontWeight="normal"
								color={disabled ? 'brand.input.placeholder' : 'brand.input.text'}
								textAlign="left"
								lineHeight="short"
							>
								Drag or{' '}
								<Text as="span" fontWeight="bold">
									Choose File
								</Text>
							</Text>
							{displayError && !disabled && <MdError size="16" color={colors.errors} />}
						</Flex>
					</Flex>
				</Box>
			</Tooltip>
		</Box>
	);
};

export default FaceliftDragDrop;
